import React from 'react';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';
import { Modal, Spin } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import BwgButton from '../../atoms/Button';
import {
  ModalContent,
  PrintableArea,
  Header,
  HeaderTop,
  CompanyInfo,
  ReportInfo,
  Logo,
  Title,
  CostCenter,
  CostCenterHeader,
  PaymentStatement,
  ButtonContainer,
  NoDataMessage,
} from './styles';

import CooperatorInfo from './CooperatorInfo';
import StatementEntriesTable from './StatementEntriesTable';
import CooperatorTotals from './CooperatorTotals';
import CooperativeActsTable from './CooperativeActsTable';
import CostCenterTotals from './CostCenterTotals';
import GrandTotal from './GrandTotal';

function PaymentStatementModal({ open, data, loading, onClose }) {
  const contentRef = React.useRef();

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: '',
    pageStyle: `
      @page {
        size: A4 portrait;
        margin-top: 0;
        margin-left: 0;
        margin-right: 0;
        margin-bottom: 5mm;

      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  const renderCooperator = (cooperator) => (
    <PaymentStatement key={cooperator.matricula}>
      <CooperatorInfo cooperator={cooperator} />
      <StatementEntriesTable entries={cooperator.lancamentos} />
      <CooperatorTotals cooperator={cooperator} />
      <CooperativeActsTable acts={cooperator.atosCooperativos} />
    </PaymentStatement>
  );

  const renderCostCenter = (costCenter, isSingleCostCenter) => {
    const cooperators = costCenter.cooperators ? Object.values(costCenter.cooperators) : [];

    return (
      <CostCenter key={costCenter.key}>
        <CostCenterHeader>
          <h2>
            <strong>
              Centro de custo: {costCenter.key} – {costCenter.description}
            </strong>
          </h2>
        </CostCenterHeader>
        {cooperators.map((cooperator) => renderCooperator(cooperator))}
        <CostCenterTotals
          costCenter={costCenter}
          isSingleCostCenter={isSingleCostCenter}
        />
      </CostCenter>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Carregando demonstrativo...</p>
        </div>
      );
    }

    if (!data) {
      return <NoDataMessage>Nenhum dado disponível</NoDataMessage>;
    }

    const costCenters = data.costCenters ? Object.values(data.costCenters) : [];
    const isSingleCostCenter = costCenters.length === 1;

    return (
      <PrintableArea ref={contentRef}>
        <table style={{ margin: 'auto' }}>
          <thead>
            <tr>
              <th>
                <Header>
                  <HeaderTop>
                    {process.env.REACT_APP_COMPLETE_LOGO_COOP && (
                      <div style={{ padding: '5px 15px' }}>
                        <Logo
                          src={process.env.REACT_APP_COMPLETE_LOGO_COOP}
                          alt="Logo"
                        />
                      </div>
                    )}
                    <CompanyInfo>
                      <p>Empresa: {data.empresa || '-'}</p>
                      <p>Folha: {data.folha || '-'}</p>
                      <p>Id Sequencial: {data.idSequencial || '-'}</p>
                      <p>LogTransação Folha: {data.gaTransactionLogId || '-'}</p>
                      <p>LogTransação: {data.gaBorderoId || '-'}</p>
                    </CompanyInfo>
                    <ReportInfo>
                      <p>Gerado por: {data.geradoPor || '-'}</p>
                      <p>{data.dataGeracao || '-'}</p>
                      <p>REF..: {data.dataReferencia || '-'}</p>
                    </ReportInfo>
                  </HeaderTop>
                  <Title>Demonstrativo de Folha Pagamento</Title>
                </Header>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {costCenters.length > 0 ? (
                  costCenters.map((costCenter) => renderCostCenter(costCenter, isSingleCostCenter))
                ) : (
                  <NoDataMessage>Nenhum centro de custo encontrado</NoDataMessage>
                )}
                <GrandTotal data={data} />
              </td>
            </tr>
          </tbody>
        </table>
      </PrintableArea>
    );
  };

  return (
    <Modal
      title="Demonstrativo de Pagamento"
      open={open}
      onCancel={onClose}
      width="900px"
      style={{ top: 20 }}
      footer={null}
    >
      <ModalContent>{renderContent()}</ModalContent>

      {!loading && data && (
        <ButtonContainer>
          <BwgButton onClick={onClose}>Fechar</BwgButton>
          <BwgButton
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Imprimir / Salvar PDF
          </BwgButton>
        </ButtonContainer>
      )}
    </Modal>
  );
}

PaymentStatementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    empresa: PropTypes.string,
    folha: PropTypes.string,
    idSequencial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gaTransactionLogId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gaBorderoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    geradoPor: PropTypes.string,
    dataGeracao: PropTypes.string,
    dataReferencia: PropTypes.string,
    totalVencimento: PropTypes.number,
    totalDesconto: PropTypes.number,
    totalCooperados: PropTypes.number,
    totalLiquido: PropTypes.number,
    lancamentosGeraisCredito: PropTypes.arrayOf(
      PropTypes.shape({
        codigo: PropTypes.string,
        descricao: PropTypes.string,
        quantidade: PropTypes.number,
        valor: PropTypes.number,
      })
    ),
    lancamentosGeraisDebito: PropTypes.arrayOf(
      PropTypes.shape({
        codigo: PropTypes.string,
        descricao: PropTypes.string,
        quantidade: PropTypes.number,
        valor: PropTypes.number,
      })
    ),
    atosCooperativosGerais: PropTypes.arrayOf(
      PropTypes.shape({
        codigo: PropTypes.string,
        descricao: PropTypes.string,
        valor: PropTypes.number,
      })
    ),
    costCenters: PropTypes.objectOf(
      PropTypes.shape({
        key: PropTypes.string,
        description: PropTypes.string,
        cooperators: PropTypes.objectOf(
          PropTypes.shape({
            nomeCooperado: PropTypes.string,
            matricula: PropTypes.string,
            atividade: PropTypes.string,
            quantidadeDependentes: PropTypes.number,
            dataAssociacao: PropTypes.string,
            fl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            ccn: PropTypes.string,
            totalCreditoAtual: PropTypes.number,
            totalCreditoAcumulado: PropTypes.number,
            totalDebitoAtual: PropTypes.number,
            totalDebitoAcumulado: PropTypes.number,
            totalLiquido: PropTypes.number,
            lancamentos: PropTypes.arrayOf(
              PropTypes.shape({
                codigo: PropTypes.string,
                descricao: PropTypes.string,
                creditoAtual: PropTypes.number,
                creditoAcumulado: PropTypes.number,
                debitoAtual: PropTypes.number,
                debitoAcumulado: PropTypes.number,
              })
            ),
            atosCooperativos: PropTypes.arrayOf(
              PropTypes.shape({
                codigo: PropTypes.string,
                descricao: PropTypes.string,
                valor: PropTypes.number,
              })
            ),
          })
        ),
        totalSection: PropTypes.shape({
          cooperatorsCount: PropTypes.number,
          totalCredit: PropTypes.number,
          totalDebit: PropTypes.number,
          netTotal: PropTypes.number,
          creditFunds: PropTypes.objectOf(
            PropTypes.shape({
              key: PropTypes.string,
              description: PropTypes.string,
              amount: PropTypes.number,
              value: PropTypes.number,
            })
          ),
          debitFunds: PropTypes.objectOf(
            PropTypes.shape({
              key: PropTypes.string,
              description: PropTypes.string,
              amount: PropTypes.number,
              value: PropTypes.number,
            })
          ),
          cooperativeActs: PropTypes.objectOf(
            PropTypes.shape({
              key: PropTypes.string,
              description: PropTypes.string,
              value: PropTypes.number,
            })
          ),
        }),
      })
    ),
  }),
};

PaymentStatementModal.defaultProps = {
  data: null,
};

export default PaymentStatementModal;
